import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Shield, AlertCircle, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const [loginMode, setLoginMode] = useState(searchParams.get("org") ? "email" : "google"); // "google" | "email" | "forgot"
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [forgotSent, setForgotSent] = useState("");
  const { checkAuth } = useAuth();

  const handleForgot = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setEmailError("");
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/org/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) setForgotSent(data.message);
      else setEmailError(data.detail || "Something went wrong");
    } catch {
      setEmailError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (isLoading) return;
    setIsLoading(true);
    setTimeout(() => {
      window.location.href = `${API}/auth/login/google`;
    }, 100);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setEmailError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/org/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        await checkAuth();
        window.location.href = data.user?.must_change_password ? "/set-password" : "/analytics";
      } else {
        const data = await response.json();
        setEmailError(data.detail || "Login failed");
      }
    } catch (err) {
      setEmailError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(/login-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      
      <div className="w-full max-w-md relative z-10 px-4 sm:px-0">
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 border border-white/20">
          <div className="flex flex-col items-center mb-6">
            <div className="w-56 h-24 sm:w-72 sm:h-32 overflow-hidden flex items-center justify-center mb-2">
              <img 
                src="/dolphin-login-logo.png"
                alt="Dolphin Retail Media"
                className="w-full object-contain"
              />
            </div>
            <h1 
              className="text-xs font-bold tracking-[0.2em] text-[#006994] mt-1"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              DOLPHIN VENUE VISION
            </h1>
            <p className="text-slate-500 text-sm mt-2">Operations Portal</p>
          </div>

          {(error || emailError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-red-600">
                  {emailError || (error === "domain" 
                    ? "Only @dolphin-digital.com email addresses are allowed."
                    : error === "auth_failed"
                    ? "Authentication failed. Please try again."
                    : error === "state_mismatch"
                    ? "Session expired. Please try again."
                    : decodeURIComponent(error)
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Login Mode Tabs */}
          <div className="flex rounded-lg bg-slate-100 p-1 mb-6" data-testid="login-mode-tabs">
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${loginMode === "google" ? "bg-white text-[#006994] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-internal-login" onClick={() => { setLoginMode("google"); setEmailError(""); }}
            >
              Internal Login
            </button>
            <button
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${loginMode !== "google" ? "bg-white text-[#006994] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              data-testid="tab-org-login" onClick={() => { setLoginMode("email"); setEmailError(""); }}
            >
              Organization Login
            </button>
          </div>

          {loginMode === "google" ? (
            <>
              <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-600">
                  Restricted to <span className="font-semibold">@dolphin-digital.com</span> emails only.
                </p>
              </div>
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full h-12 bg-[#006994] hover:bg-[#005577] text-white rounded-xl font-medium flex items-center justify-center gap-3"
              >
                {isLoading ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </Button>
            </>
          ) : loginMode === "forgot" ? (
            forgotSent ? (
              <div data-testid="forgot-sent">
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm text-emerald-700">{forgotSent} The link is valid for 1 hour.</p>
                </div>
                <Button onClick={() => { setLoginMode("email"); setForgotSent(""); }} className="w-full h-12 bg-[#006994] hover:bg-[#005577] text-white rounded-xl" data-testid="forgot-back-btn">
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-4">
                <p className="text-sm text-slate-600">Enter your email and we'll send you a link to reset your password.</p>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@organization.com" required data-testid="forgot-email-input"
                    className="w-full pl-10 pr-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                  />
                </div>
                <Button type="submit" disabled={isLoading} data-testid="forgot-submit-btn" className="w-full h-12 bg-[#006994] hover:bg-[#005577] text-white rounded-xl font-medium">
                  {isLoading ? "Sending..." : "Send Reset Link"}
                </Button>
                <button type="button" onClick={() => { setLoginMode("email"); setEmailError(""); }} className="w-full text-sm text-[#006994] hover:underline" data-testid="forgot-cancel-btn">
                  Back to sign in
                </button>
              </form>
            )
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@organization.com"
                    required
                    data-testid="org-email-input"
                    className="w-full pl-10 pr-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    required
                    data-testid="org-password-input"
                    className="w-full pl-10 pr-10 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006994]/20 focus:border-[#006994]"
                  />
                  <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                data-testid="org-login-btn"
                className="w-full h-12 bg-[#006994] hover:bg-[#005577] text-white rounded-xl font-medium"
              >
                {isLoading ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                ) : "Sign In"}
              </Button>
              <button type="button" onClick={() => { setLoginMode("forgot"); setEmailError(""); }} className="w-full text-sm text-[#006994] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-center">
            <p className="text-xs text-slate-400">Dolphin Digital OOH Media &copy; 2026</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
